// ============================================================================
// EXCEL / CSV IMPORT
// ============================================================================
// Upload Excel or CSV files to import emissions data into bulk sessions
// ============================================================================

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  Check,
  AlertCircle,
  ChevronDown,
  X,
  Loader2,
  Plus,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ParsedPreview {
  columns: string[];
  rows: Record<string, string>[];
  totalRows: number;
}

interface BulkSession {
  id: number;
  session_name: string;
  description?: string;
  status?: string;
}

interface ImportResult {
  imported: number;
  skipped: number;
  errors: string[];
}

type SystemField =
  | ''
  | 'activity_description'
  | 'activity_amount'
  | 'input_unit'
  | 'scope'
  | 'scope3_category'
  | 'notes'
  | 'emission_factor_id';

const SYSTEM_FIELDS: { value: SystemField; label: string; required: boolean }[] = [
  { value: 'activity_description', label: 'Activity Description', required: true },
  { value: 'activity_amount', label: 'Activity Amount', required: true },
  { value: 'input_unit', label: 'Input Unit', required: true },
  { value: 'scope', label: 'Scope (1 / 2 / 3)', required: false },
  { value: 'scope3_category', label: 'Scope 3 Category (1-15)', required: false },
  { value: 'notes', label: 'Notes', required: false },
  { value: 'emission_factor_id', label: 'Emission Factor ID', required: false },
];

const REQUIRED_FIELDS: SystemField[] = ['activity_description', 'activity_amount', 'input_unit'];

type Step = 'upload' | 'preview' | 'mapping' | 'session' | 'importing' | 'done';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const csrfToken = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
  return headers;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ExcelImport: React.FC = () => {
  // -- State ----------------------------------------------------------------
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState<ParsedPreview | null>(null);
  const [mapping, setMapping] = useState<Record<string, SystemField>>({});
  const [sessions, setSessions] = useState<BulkSession[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<number | 'new'>('new');
  const [newSessionName, setNewSessionName] = useState('');
  const [newSessionDesc, setNewSessionDesc] = useState('');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // -- Load sessions --------------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/emissions/bulk/sessions', {
          headers: getAuthHeaders(),
        });
        if (!res.ok) throw new Error('Failed to load sessions');
        const body = await res.json();
        const list = body?.data ?? body ?? [];
        setSessions(Array.isArray(list) ? list : []);
      } catch {
        // non-critical
      }
    })();
  }, []);

  // -- File handling --------------------------------------------------------
  const handleFile = useCallback((f: File) => {
    const ext = f.name.split('.').pop()?.toLowerCase() ?? '';
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      setError('Please upload a .xlsx, .xls, or .csv file.');
      return;
    }
    setFile(f);
    setError(null);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (e.dataTransfer.files.length) handleFile(e.dataTransfer.files[0]);
    },
    [handleFile],
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files?.length) handleFile(e.target.files[0]);
    },
    [handleFile],
  );

  // -- Parse upload ---------------------------------------------------------
  const parseFile = useCallback(async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch('/api/emissions/bulk/upload/parse', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message ?? `Parse failed (${res.status})`);
      }
      const body = await res.json();
      const raw: any = body?.data ?? body ?? {};
      // Normalise the shape. The backend returns
      //   { headers: string[], preview: Record<string,string>[], totalRows: number }
      // but this component expects
      //   { columns: string[], rows: Record<string,string>[], totalRows: number }
      // Map the backend keys → frontend keys, and accept either shape so
      // a future API change in either direction won't break the UI.
      const rawColumns: string[] = Array.isArray(raw.columns) ? raw.columns
        : Array.isArray(raw.headers) ? raw.headers
        : [];
      const rawRows: Record<string, string>[] = Array.isArray(raw.rows) ? raw.rows
        : Array.isArray(raw.preview) ? raw.preview
        : [];
      const data: ParsedPreview = {
        columns: rawColumns,
        rows: rawRows,
        totalRows: typeof raw.totalRows === 'number' ? raw.totalRows : rawRows.length,
      };
      setPreview(data);

      // Auto-guess mapping from column names
      const autoMap: Record<string, SystemField> = {};
      (data.columns ?? []).forEach((col) => {
        const lower = col.toLowerCase().replace(/[^a-z0-9]/g, '_');
        for (const sf of SYSTEM_FIELDS) {
          if (lower.includes(sf.value) || sf.value.includes(lower)) {
            autoMap[col] = sf.value;
            break;
          }
        }
      });
      setMapping(autoMap);
      setStep('preview');
    } catch (err: any) {
      setError(err?.message ?? 'Failed to parse file');
    } finally {
      setLoading(false);
    }
  }, [file]);

  // -- Import ---------------------------------------------------------------
  const doImport = useCallback(async () => {
    if (!file || !preview) return;

    // Validate required fields are mapped
    const mappedFields = Object.values(mapping);
    const missing = REQUIRED_FIELDS.filter((f) => !mappedFields.includes(f));
    if (missing.length) {
      setError(
        `Required fields not mapped: ${missing
          .map((f) => SYSTEM_FIELDS.find((sf) => sf.value === f)?.label ?? f)
          .join(', ')}`,
      );
      return;
    }

    setStep('importing');
    setLoading(true);
    setError(null);

    try {
      let sessionId: number;

      if (selectedSessionId === 'new') {
        if (!newSessionName.trim()) {
          setError('Please enter a session name.');
          setStep('session');
          setLoading(false);
          return;
        }
        // Create a new session first
        const createRes = await fetch('/api/emissions/bulk/sessions', {
          method: 'POST',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_name: newSessionName.trim(),
            description: newSessionDesc.trim() || undefined,
          }),
        });
        if (!createRes.ok) {
          const err = await createRes.json().catch(() => null);
          throw new Error(err?.message ?? 'Failed to create session');
        }
        const created = await createRes.json();
        sessionId = (created?.data ?? created)?.id;
        if (!sessionId) throw new Error('Session created but no ID returned');
      } else {
        sessionId = selectedSessionId;
      }

      // Build import request
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mapping', JSON.stringify(mapping));

      const res = await fetch(`/api/emissions/bulk/upload/${sessionId}/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message ?? `Import failed (${res.status})`);
      }

      const body = await res.json();
      const result: ImportResult = body?.data ?? body;
      setImportResult(result);
      setStep('done');
    } catch (err: any) {
      setError(err?.message ?? 'Import failed');
      setStep('session');
    } finally {
      setLoading(false);
    }
  }, [file, preview, mapping, selectedSessionId, newSessionName, newSessionDesc]);

  // -- Reset ----------------------------------------------------------------
  const reset = useCallback(() => {
    setStep('upload');
    setFile(null);
    setPreview(null);
    setMapping({});
    setImportResult(null);
    setError(null);
    setSelectedSessionId('new');
    setNewSessionName('');
    setNewSessionDesc('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // -- Mapping validation ---------------------------------------------------
  const mappedFields = Object.values(mapping).filter(Boolean);
  const allRequiredMapped = REQUIRED_FIELDS.every((f) => mappedFields.includes(f));

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
          <FileSpreadsheet className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Import Emissions Data</h1>
          <p className="text-sm text-slate-500">
            Upload an Excel or CSV file to import emissions data into a bulk calculation session.
          </p>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-sm">
        {(['upload', 'preview', 'mapping', 'session', 'done'] as Step[]).map((s, i, arr) => {
          const labels: Record<string, string> = {
            upload: '1. Upload',
            preview: '2. Preview',
            mapping: '3. Map Columns',
            session: '4. Session',
            done: '5. Import',
          };
          const stepOrder = arr.indexOf(step === 'importing' ? 'done' : step);
          const thisOrder = i;
          const isActive = thisOrder === stepOrder;
          const isDone = thisOrder < stepOrder || step === 'done';
          return (
            <React.Fragment key={s}>
              {i > 0 && <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />}
              <span
                className={`px-3 py-1 rounded-full font-medium whitespace-nowrap ${
                  isActive
                    ? 'bg-emerald-100 text-emerald-700'
                    : isDone
                      ? 'bg-emerald-50 text-emerald-500'
                      : 'bg-slate-100 text-slate-400'
                }`}
              >
                {isDone && !isActive && <Check className="w-3 h-3 inline mr-1 -mt-0.5" />}
                {labels[s]}
              </span>
            </React.Fragment>
          );
        })}
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
          <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 1: Upload                                                    */}
      {/* ================================================================= */}
      {step === 'upload' && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={handleFileInput}
          />

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`flex flex-col items-center justify-center gap-4 p-12 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
              dragOver
                ? 'border-emerald-400 bg-emerald-50'
                : file
                  ? 'border-emerald-300 bg-emerald-50/50'
                  : 'border-slate-300 hover:border-slate-400 bg-slate-50/50'
            }`}
          >
            {file ? (
              <>
                <FileSpreadsheet className="w-12 h-12 text-emerald-500" />
                <div className="text-center">
                  <p className="font-semibold text-slate-800">{file.name}</p>
                  <p className="text-sm text-slate-500">{formatBytes(file.size)}</p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="text-sm text-slate-500 hover:text-red-500 underline"
                >
                  Remove file
                </button>
              </>
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-400" />
                <div className="text-center">
                  <p className="font-semibold text-slate-700">
                    Drag and drop your file here, or click to browse
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Supports .xlsx, .xls, and .csv files
                  </p>
                </div>
              </>
            )}
          </div>

          {file && (
            <div className="mt-6 flex justify-end">
              <button
                onClick={parseFile}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4" />
                )}
                {loading ? 'Parsing...' : 'Parse File'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 2: Preview                                                   */}
      {/* ================================================================= */}
      {step === 'preview' && preview && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <h2 className="font-semibold text-slate-800">File Preview</h2>
              <p className="text-sm text-slate-500">
                Showing first {(preview.rows ?? []).length} of {preview.totalRows ?? 0} rows &middot;{' '}
                {(preview.columns ?? []).length} columns detected
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    #
                  </th>
                  {preview.columns.map((col) => (
                    <th
                      key={col}
                      className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {preview.rows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-slate-50/50">
                    <td className="px-4 py-2 text-left text-slate-400">{idx + 1}</td>
                    {preview.columns.map((col) => (
                      <td key={col} className="px-4 py-2 text-slate-700 whitespace-nowrap max-w-[200px] truncate">
                        {row[col] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-slate-100 flex justify-between">
            <button
              onClick={reset}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Start Over
            </button>
            <button
              onClick={() => setStep('mapping')}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              Map Columns
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 3: Column Mapping                                            */}
      {/* ================================================================= */}
      {step === 'mapping' && preview && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Column Mapping</h2>
            <p className="text-sm text-slate-500">
              Map each file column to its corresponding system field. Fields marked with * are required.
            </p>
          </div>

          <div className="p-6 space-y-3">
            {preview.columns.map((col) => (
              <div
                key={col}
                className="flex items-center gap-4 p-3 rounded-lg border border-slate-100 hover:border-slate-200 transition-colors"
              >
                <div className="w-1/3">
                  <span className="text-sm font-medium text-slate-700">{col}</span>
                  {preview.rows[0]?.[col] && (
                    <p className="text-xs text-slate-400 mt-0.5 truncate">
                      e.g. &quot;{preview.rows[0][col]}&quot;
                    </p>
                  )}
                </div>

                <ArrowRight className="w-4 h-4 text-slate-300 flex-shrink-0" />

                <div className="w-1/2 relative">
                  <select
                    value={mapping[col] ?? ''}
                    onChange={(e) =>
                      setMapping((prev) => ({
                        ...prev,
                        [col]: e.target.value as SystemField,
                      }))
                    }
                    className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 pr-8"
                  >
                    <option value="">-- Skip this column --</option>
                    {SYSTEM_FIELDS.map((sf) => {
                      const alreadyUsed =
                        Object.entries(mapping).some(
                          ([k, v]) => v === sf.value && k !== col,
                        );
                      return (
                        <option key={sf.value} value={sf.value} disabled={alreadyUsed}>
                          {sf.label}
                          {sf.required ? ' *' : ''}
                          {alreadyUsed ? ' (already mapped)' : ''}
                        </option>
                      );
                    })}
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>

                {mapping[col] && (
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>

          {/* Mapping summary */}
          <div className="px-6 pb-2">
            <div className="flex flex-wrap gap-2">
              {REQUIRED_FIELDS.map((rf) => {
                const mapped = mappedFields.includes(rf);
                return (
                  <span
                    key={rf}
                    className={`text-xs px-2 py-1 rounded-full font-medium ${
                      mapped
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-red-100 text-red-600'
                    }`}
                  >
                    {mapped ? <Check className="w-3 h-3 inline mr-0.5 -mt-0.5" /> : null}
                    {SYSTEM_FIELDS.find((sf) => sf.value === rf)?.label}
                    {!mapped && ' (required)'}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="px-6 py-4 border-t border-slate-100 flex justify-between">
            <button
              onClick={() => setStep('preview')}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={() => {
                if (!allRequiredMapped) {
                  setError('Please map all required fields before continuing.');
                  return;
                }
                setStep('session');
              }}
              disabled={!allRequiredMapped}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Select Session
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 4: Session Selection                                         */}
      {/* ================================================================= */}
      {step === 'session' && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Select Session</h2>
            <p className="text-sm text-slate-500">
              Choose an existing bulk session or create a new one.
            </p>
          </div>

          <div className="p-6 space-y-4">
            <div className="relative">
              <select
                value={selectedSessionId}
                onChange={(e) =>
                  setSelectedSessionId(
                    e.target.value === 'new' ? 'new' : Number(e.target.value),
                  )
                }
                className="w-full appearance-none bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 pr-8"
              >
                <option value="new">+ Create new session</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.session_name}
                    {s.status ? ` (${s.status})` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>

            {selectedSessionId === 'new' && (
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Session Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={newSessionName}
                    onChange={(e) => setNewSessionName(e.target.value)}
                    placeholder="e.g. Q1 2026 Emissions Import"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={newSessionDesc}
                    onChange={(e) => setNewSessionDesc(e.target.value)}
                    placeholder="Optional description"
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-slate-100 flex justify-between">
            <button
              onClick={() => setStep('mapping')}
              className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Back
            </button>
            <button
              onClick={doImport}
              disabled={loading || (selectedSessionId === 'new' && !newSessionName.trim())}
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {loading ? 'Importing...' : 'Import Data'}
            </button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 5: Importing / Progress                                      */}
      {/* ================================================================= */}
      {step === 'importing' && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-12">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mx-auto" />
          <p className="mt-4 font-semibold text-slate-800">Importing data...</p>
          <p className="text-sm text-slate-500 mt-1">
            Processing your file and creating emission entries.
          </p>
        </div>
      )}

      {/* ================================================================= */}
      {/* STEP 6: Done                                                      */}
      {/* ================================================================= */}
      {step === 'done' && importResult && (
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-8">
          <div className="text-center">
            <div className="mx-auto w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">Import Complete</h2>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-emerald-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-emerald-700">{importResult.imported}</p>
              <p className="text-sm text-emerald-600 mt-0.5">Rows Imported</p>
            </div>
            <div className="p-4 bg-amber-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-amber-700">{importResult.skipped}</p>
              <p className="text-sm text-amber-600 mt-0.5">Rows Skipped</p>
            </div>
            <div className="p-4 bg-slate-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-slate-700">
                {importResult.errors?.length ?? 0}
              </p>
              <p className="text-sm text-slate-500 mt-0.5">Errors</p>
            </div>
          </div>

          {importResult.errors?.length > 0 && (
            <div className="mt-4 p-4 bg-red-50 rounded-xl border border-red-200">
              <p className="text-sm font-medium text-red-700 mb-2">Errors</p>
              <ul className="text-sm text-red-600 list-disc list-inside space-y-1 max-h-40 overflow-y-auto">
                {importResult.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-6 flex justify-center gap-3">
            <a
              href="/emissions/bulk-calculator"
              className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
            >
              View in Bulk Calculator →
            </a>
            <button
              onClick={reset}
              className="flex items-center gap-2 px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Import Another File
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelImport;
